import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminZonesPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminZonesPage() {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingZone, setDeletingZone] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    shipping_price: "",
  });

  // ===== Helpers =====
  const getToken = () =>
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";

  useEffect(() => {
    const token = getToken();
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    fetchZones(token);
  }, [navigate]);

  const fetchZones = async (token) => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      };

      const response = await fetch(`${API_BASE}/zones`, { headers });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setZones(data.zones || []);
        } else {
          setZones([]);
        }
      } else {
        setZones([]);
      }
    } catch (error) {
      toast.error("Error fetching zones");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    setIsSubmitting(true);

    // Validate required fields
    if (!formData.name || !formData.shipping_price) {
      toast.error("Please fill in all required fields");
      setIsSubmitting(false);
      return;
    }

    // Validate shipping price
    if (isNaN(formData.shipping_price) || parseFloat(formData.shipping_price) < 0) {
      toast.error("Please enter a valid shipping price");
      setIsSubmitting(false);
      return;
    }

    // Check for duplicate zone name (client-side validation)
    // Only check for duplicates when creating a new zone or when the name has actually changed
    if (!editingZone || formData.name !== editingZone.name) {
      const existingZone = zones.find(zone => 
        zone.name.toLowerCase().trim() === formData.name.toLowerCase().trim() && 
        zone.id !== editingZone?.id
      );
      
      if (existingZone) {
        toast.error("A zone with this name already exists. Please choose a different name.");
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const url = editingZone
        ? `${API_BASE}/zones/${editingZone.id}`
        : `${API_BASE}/zones`;
      
      const method = editingZone ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          toast.success(editingZone ? "Zone updated successfully" : "Zone created successfully");
          setShowModal(false);
          setEditingZone(null);
          resetForm();
          fetchZones(token);
        } else {
          toast.error(data.message || "Failed to save zone");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (errorData?.errors && typeof errorData.errors === "object") {
          const firstKey = Object.keys(errorData.errors)[0];
          const firstMsg = errorData.errors[firstKey]?.[0];
          
          // Handle specific validation errors
          if (firstKey === "name" && firstMsg?.includes("already been taken")) {
            if (editingZone) {
              toast.error("A zone with this name already exists. Please choose a different name.");
            } else {
              toast.error("A zone with this name already exists. Please choose a different name.");
            }
          } else {
            toast.error(firstMsg || errorData.message || "Failed to save zone");
          }
        } else {
          toast.error(errorData.message || "Failed to save zone");
        }
      }
    } catch (error) {
      toast.error("Error saving zone");
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (zone) => {
    setEditingZone(zone);
    setFormData({
      name: zone.name ?? "",
      shipping_price: zone.shipping_price ?? "",
    });
    setShowModal(true);
  };

  const handleDelete = async (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    setDeletingZone(zone);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingZone) return;
    
    setIsDeleting(true);
    const token = getToken();

    try {
      const res = await fetch(`${API_BASE}/zones/${deletingZone.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success("Zone deleted successfully");
          setShowDeleteModal(false);
          setDeletingZone(null);
          fetchZones(token);
        } else {
          toast.error(data.message || "Failed to delete zone");
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.message || "Failed to delete zone");
      }
    } catch (error) {
      toast.error("Error deleting zone");
      console.error("Error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingZone(null);
    setIsDeleting(false);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      shipping_price: "",
    });
  };

  const openModal = () => {
    setEditingZone(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingZone(null);
    resetForm();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-zones">
          <div className="loading">Loading zones...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-zones">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <h1>Zone Management</h1>
            <p>Manage shipping zones and their prices</p>
          </div>
          <div className="admin-actions">
            <button className="btn-primary" onClick={openModal}>
              <svg viewBox="0 0 24 24">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" />
              </svg>
              Add New Zone
            </button>
          </div>
        </div>

        {/* Zones Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Zone Name</th>
                <th>Shipping Price</th>
                <th>Addresses Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {zones.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-state">
                    No zones found
                  </td>
                </tr>
              ) : (
                zones.map((zone) => (
                  <tr key={zone.id}>
                    <td>{zone.id}</td>
                    <td>
                      <div className="zone-name">
                        <strong>{zone.name}</strong>
                      </div>
                    </td>
                    <td>
                      <div className="shipping-price">
                        <span className="price">${zone.shipping_price}</span>
                      </div>
                    </td>
                    <td>
                      <div className="addresses-count">
                        <span className={`count ${zone.addresses_count > 0 ? 'has-addresses' : 'no-addresses'}`}>
                          {zone.addresses_count}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(zone)}
                          title="Edit"
                        >
                          <svg viewBox="0 0 24 24">
                            <path
                              d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(zone.id)}
                          title="Delete"
                          disabled={zone.addresses_count > 0}
                        >
                          <svg viewBox="0 0 24 24">
                            <path
                              d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <line
                              x1="10"
                              y1="11"
                              x2="10"
                              y2="17"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <line
                              x1="14"
                              y1="11"
                              x2="14"
                              y2="17"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingZone ? "Edit Zone" : "Add New Zone"}</h2>
                <button className="modal-close" onClick={closeModal}>
                  <svg viewBox="0 0 24 24">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit} className="modal-form">
                  <div className="form-group">
                    <label htmlFor="name">Zone Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter zone name (e.g., Beirut, Mount Lebanon)"
                    />
                    <small className="form-help">
                      Zone names must be unique. Choose a descriptive name for the delivery area.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="shipping_price">Shipping Price</label>
                    <input
                      type="number"
                      id="shipping_price"
                      name="shipping_price"
                      value={formData.shipping_price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                      placeholder="0.00"
                    />
                    <small className="form-help">
                      Enter the shipping cost for this zone in USD
                    </small>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader size={16} strokeWidth={2} />
                          {editingZone ? "Updating..." : "Creating..."}
                        </>
                      ) : (
                        editingZone ? "Update Zone" : "Create Zone"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title="Delete Zone"
          message="Are you sure you want to delete this zone?"
          itemName={deletingZone ? deletingZone.name : ""}
          isLoading={isDeleting}
        />
      </div>
    </AdminLayout>
  );
}
